import React from 'react';
import { orgHasContractTypes } from '../constants/assessmentTypesByOrg';

/**
 * PatientContractTypeBadge
 *
 * A small, read-only pill that surfaces the "project" a patient was referred
 * through — stored on the patient record as `customFields.assessmentType` and
 * labelled "Contract Type" throughout patient setup/edit and the dashboard.
 *
 * Why this exists
 * ---------------
 * Clinicians previously had to leave the platform and check the client's own
 * systems to work out which contract/project a patient belonged to. That is
 * risky during appointments (e.g. Post-Assessment Discussion) where the
 * available support, report wording and next-steps process differ per project.
 * This badge puts that context on-screen at the point of care, in a consistent
 * place across the patient's key screens, so it becomes muscle memory.
 *
 * Design decisions (deliberately kept minimal)
 * --------------------------------------------
 * - It is context, not status: a single muted neutral pill, never coloured per
 *   project, so it can't be mistaken for an alert/warning/workflow state and so
 *   it scales to an unlimited number of client-defined projects.
 * - The value is shown EXACTLY as stored. We never abbreviate, rename or
 *   validate it against the org's current option list, so legacy/retired
 *   contract strings still resolve on historical patients.
 * - Long project names (e.g. "Cambridge & Peterborough Integrated Health
 *   Board") are capped in width and truncated with an ellipsis; the full value
 *   is always available via the native browser tooltip (`title`).
 *
 * Gating
 * ------
 * Only organisations that categorise patients by contract type (see
 * `orgHasContractTypes`) ever show the badge. For every other org (SLaM,
 * Phoenix, etc.) this renders nothing, even if a stray value exists on the
 * record. Pass the CLINICIAN's organisation (from auth claims) as `organisation`
 * so the badge matches the same orgs that get the dashboard/analytics contract
 * UI. The value itself comes from the patient record.
 *
 * Error state
 * -----------
 * Contract Type is mandatory at patient creation, so a missing value should be
 * rare. When it does happen (legacy data / data issue) we show an explicit
 * muted "Contract type not set" rather than silently rendering nothing, so the
 * absence is never mistaken for valid behaviour.
 *
 * Optional preface label
 * ------------------------
 * Passing `label` (e.g. "Contract") renders a muted preface before the pill, so
 * it reads "Contract: National RTC". The preface is decorative and hidden from
 * assistive tech (the pill already carries the full "Contract type: …"
 * accessible name), which avoids screen readers announcing "Contract" twice.
 * When a preface is shown, the missing-value pill is kept short ("Not set")
 * because the preface already supplies the "Contract" context; the value-only
 * form (no label) spells it out in full so the pill is self-describing.
 *
 * Subcontract (optional second pill)
 * ---------------------------------
 * When assessmentType is "National RTC", some orgs also capture an ICB
 * sub-region as `customFields.subContractType`.  Pass it via the
 * `subContractType` prop to render a second pill inline (same muted neutral
 * style) so clinicians see both the contract and the commissioning region
 * without leaving the screen.  The prop is fully optional and
 * backward-compatible: call sites that omit it render exactly as before.
 *
 * "N/A" is a valid stored value (the clinician recorded that there is no
 * ICB to attach) but it is not useful on-screen as a second pill, so it is
 * treated the same as a missing subcontract and hidden everywhere this
 * badge is used.
 *
 * Styling: the subcontract pill is a shade darker (gray-200 fill, gray-300
 * border) so it reads as related-but-secondary to the contract pill rather
 * than a duplicate of it. Meaning still comes from the accessible name
 * ("Subcontract: …"), not from colour alone.
 *
 * @param {object} props
 * @param {string} [props.organisation] Clinician's organisation (auth claims).
 * @param {string} [props.assessmentType] Raw `customFields.assessmentType` value.
 * @param {string} [props.subContractType] Raw `customFields.subContractType` value.
 * @param {string} [props.label] Optional visible preface, e.g. "Contract".
 * @param {boolean} [props.stack] Stack pills vertically (dashboard table cells).
 * @param {string} [props.className] Optional extra classes for positioning.
 */
export default function PatientContractTypeBadge({
  organisation,
  assessmentType,
  subContractType,
  label,
  stack = false,
  className = '',
}) {
  // Hard gate: orgs without configured contract types never see this UI.
  if (!orgHasContractTypes(organisation)) {
    return null;
  }

  // Treat null/undefined/blank/whitespace-only as "not set".
  const trimmedValue = typeof assessmentType === 'string' ? assessmentType.trim() : '';
  const hasValue = trimmedValue.length > 0;

  // Visible pill text. With a preface label the "not set" copy stays short; the
  // value-only form spells it out so the pill is self-describing on its own.
  const pillText = hasValue
    ? trimmedValue
    : (label ? 'Not set' : 'Contract type not set');

  // Accessible name and tooltip are always the full, self-describing phrase,
  // independent of whether the decorative preface label is shown.
  const tooltip = hasValue ? trimmedValue : 'Contract type not set';
  const ariaLabel = hasValue
    ? `Contract type: ${trimmedValue}`
    : 'Contract type not set';

  // Shared pill shape. Contract stays on the lighter muted surface;
  // subcontract uses a darker fill so the two pills are related but not
  // identical when they sit next to each other. Dashboard table cells pass
  // `stack` so two long ICB names sit one above the other.
  //
  // `inline-block` (not inline-flex) is required for truncation: Tailwind's
  // `truncate` is overflow-hidden + nowrap + ellipsis, and ellipsis only
  // paints on a block formatting context. On a flex container the text is
  // clipped at the rounded edge with no "...". Native `title` still shows
  // the full value on hover.
  const pillMaxWidth = stack ? 'max-w-[11rem]' : 'max-w-[16rem]';
  const pillShape =
    `inline-block ${pillMaxWidth} truncate rounded-full px-2.5 py-0.5 text-xs font-medium`;
  const baseClasses = `${pillShape} border border-gray-200 bg-gray-100`;
  const toneClass = hasValue ? 'text-gray-700' : 'text-gray-500';
  const wrapDirection = stack
    ? 'inline-flex flex-col items-start gap-1'
    : 'inline-flex items-center gap-1.5';
  const subClasses = `${pillShape} border border-gray-300 bg-gray-200 text-gray-800`;

  // Subcontract pill (ICB region for National RTC patients). Rendered as a
  // second pill. Hidden when the value is missing/blank OR the explicit
  // "N/A" option — that option means "no ICB to record", so showing it as
  // its own badge would just add noise next to the contract pill.
  const trimmedSub = typeof subContractType === 'string' ? subContractType.trim() : '';
  const hasSub = trimmedSub.length > 0 && trimmedSub.toUpperCase() !== 'N/A';
  const subPill = hasSub ? (
    <span
      className={subClasses}
      title={trimmedSub}
      aria-label={`Subcontract: ${trimmedSub}`}
    >
      {trimmedSub}
    </span>
  ) : null;

  // Value-only form (no preface): wrap in a flex row when a subcontract pill
  // is also present, otherwise keep the original single-span output so
  // positioning classes continue to apply directly to the pill.
  if (!label) {
    if (!subPill) {
      const combinedClassName = [baseClasses, toneClass, className]
        .filter(Boolean)
        .join(' ');
      return (
        <span className={combinedClassName} title={tooltip} aria-label={ariaLabel}>
          {pillText}
        </span>
      );
    }
    const wrapClassName = [wrapDirection, className]
      .filter(Boolean)
      .join(' ');
    return (
      <span className={wrapClassName}>
        <span
          className={[baseClasses, toneClass].join(' ')}
          title={tooltip}
          aria-label={ariaLabel}
        >
          {pillText}
        </span>
        {subPill}
      </span>
    );
  }

  // Prefaced form: "Contract: [pill] [subPill]". The preface is aria-hidden so
  // screen readers hear only the pill's full accessible name.
  const wrapperClassName = [wrapDirection, className]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={wrapperClassName}>
      <span className="text-xs font-medium text-gray-500" aria-hidden="true">
        {label}:
      </span>
      <span
        className={[baseClasses, toneClass].join(' ')}
        title={tooltip}
        aria-label={ariaLabel}
      >
        {pillText}
      </span>
      {subPill}
    </span>
  );
}
